import { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { adminApi, type ArticleSource } from '@/api';

export function ArticleSourceForm() {
  const { id } = useParams<'id'>();
  const navigate = useNavigate();
  const isNew = !id;
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<Partial<ArticleSource> & { name: string }>({
    name: '',
    feedUrl: '',
    baseUrl: '',
    isActive: true,
    order: 0,
  });

  useEffect(() => {
    if (isNew) return;
    adminApi.articleSources.get(id!)
      .then((s) => setForm({ ...s, feedUrl: s.feedUrl ?? '', baseUrl: s.baseUrl ?? '' }))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [id, isNew]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSaving(true);
    try {
      if (isNew) {
        await adminApi.articleSources.create({
          name: form.name,
          feedUrl: form.feedUrl || null,
          baseUrl: form.baseUrl || null,
          isActive: form.isActive ?? true,
          order: form.order ?? 0,
        });
        navigate('/syncs/sources');
      } else {
        await adminApi.articleSources.update(id!, {
          name: form.name,
          feedUrl: form.feedUrl || null,
          baseUrl: form.baseUrl || null,
          isActive: form.isActive,
          order: form.order,
        });
        navigate('/syncs/sources');
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="text-slate-500">Loading…</div>;

  return (
    <div>
      <div className="mb-6 flex items-center gap-4">
        <Link to="/syncs/sources" className="text-slate-600 hover:text-slate-900 text-sm">
          ← Article Sources
        </Link>
        <h1 className="text-2xl font-semibold text-slate-800">{isNew ? 'New article source' : 'Edit article source'}</h1>
      </div>
      <form onSubmit={handleSubmit} className="max-w-xl space-y-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Name</label>
          <input
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            required
            className="w-full rounded-lg border border-slate-300 px-3 py-2"
            placeholder="e.g. AWS News Blog"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Feed URL (RSS/Atom, optional)</label>
          <input
            value={form.feedUrl ?? ''}
            onChange={(e) => setForm((f) => ({ ...f, feedUrl: e.target.value }))}
            type="url"
            className="w-full rounded-lg border border-slate-300 px-3 py-2"
            placeholder="https://..."
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Base URL (optional)</label>
          <input
            value={form.baseUrl ?? ''}
            onChange={(e) => setForm((f) => ({ ...f, baseUrl: e.target.value }))}
            type="url"
            className="w-full rounded-lg border border-slate-300 px-3 py-2"
            placeholder="https://..."
          />
        </div>
        <div className="flex items-center gap-2">
          <input
            id="isActive"
            type="checkbox"
            checked={form.isActive ?? true}
            onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))}
            className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
          />
          <label htmlFor="isActive" className="text-sm font-medium text-slate-700">Active</label>
        </div>
        {error && <div className="rounded-lg bg-red-50 text-red-700 px-3 py-2 text-sm">{error}</div>}
        <div className="flex gap-3">
          <button
            type="submit"
            disabled={saving}
            className="rounded-lg bg-indigo-600 px-4 py-2 text-white font-medium hover:bg-indigo-700 disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
          <Link to="/syncs/sources" className="rounded-lg border border-slate-300 px-4 py-2 text-slate-700 hover:bg-slate-50">
            Cancel
          </Link>
        </div>
      </form>
    </div>
  );
}
